import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import StudioIcon from './StudioIcon';

export default function VoiceInput({ value, onChange, disabled, onActiveChange }) {
  const { t, locale } = useI18n();
  const Recognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const session = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [language, setLanguage] = useState(locale === 'ar' ? 'ar-SA' : 'en-US');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const active = phase !== 'idle';

  const release = useCallback(() => {
    const run = session.current;
    if (!run) return;
    session.current = null;
    clearTimeout(run.timer);
    run.recognition.onstart = null;
    run.recognition.onresult = null;
    run.recognition.onerror = null;
    run.recognition.onend = null;
    try { run.recognition.abort(); } catch { /* Already stopped. */ }
    onActiveChange?.(false);
  }, [onActiveChange]);

  useEffect(() => {
    const stop = () => {
      release();
      setPhase('idle');
      setInterim('');
    };
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    window.addEventListener('pagehide', stop);
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      window.removeEventListener('pagehide', stop);
      document.removeEventListener('visibilitychange', stopWhenHidden);
      release();
    };
  }, [release]);

  useEffect(() => {
    // An external edit must never be overwritten by a late recognition event.
    if (disabled || (session.current && value !== session.current.lastValue)) {
      release();
      setPhase('idle');
      setInterim('');
    }
  }, [disabled, value, release]);

  function toggle() {
    if (disabled || !Recognition) return;
    const running = session.current;
    if (running) {
      if (phase === 'starting') {
        release();
        setPhase('idle');
        setInterim('');
      } else if (phase === 'listening') {
        setPhase('stopping');
        running.timer = setTimeout(running.finish, 5000);
        try { running.recognition.stop(); } catch { running.finish(); }
      }
      return;
    }

    setError('');
    setMessage('');
    setInterim('');
    try {
      const recognition = new Recognition();
      const run = { recognition, base: value || '', lastValue: value, hasFinal: false, timer: null };
      session.current = run;
      recognition.lang = language;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      run.finish = () => {
        if (session.current !== run) return;
        release();
        setPhase('idle');
        setInterim('');
        if (run.hasFinal) setMessage('added');
        else setError('noSpeech');
      };
      recognition.onstart = () => {
        if (session.current !== run) return;
        clearTimeout(run.timer);
        setPhase('listening');
      };
      recognition.onresult = (event) => {
        if (session.current !== run) return;
        const finalWords = [];
        const partialWords = [];
        // Results contain the whole session, including earlier final results.
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          (result.isFinal ? finalWords : partialWords).push(result[0].transcript.trim());
        }
        const spoken = finalWords.filter(Boolean).join(' ');
        if (spoken) {
          const next = [run.base.trimEnd(), spoken].filter(Boolean).join(' ');
          run.hasFinal = true;
          run.lastValue = next;
          onChange(next);
        }
        setInterim(partialWords.filter(Boolean).join(' '));
      };
      recognition.onerror = (event) => {
        if (session.current !== run) return;
        const errors = {
          'not-allowed': 'permission', 'service-not-allowed': 'service',
          'audio-capture': 'microphone', 'network': 'network',
          'no-speech': 'noSpeech', 'language-not-supported': 'language',
        };
        release();
        setPhase('idle');
        setInterim('');
        if (event.error !== 'aborted') setError(errors[event.error] || 'failed');
      };
      recognition.onend = run.finish;
      setPhase('starting');
      onActiveChange?.(true);
      run.timer = setTimeout(() => {
        if (session.current !== run) return;
        release();
        setPhase('idle');
        setError('startTimeout');
      }, 15000);
      recognition.start();
    } catch {
      release();
      setPhase('idle');
      setError('failed');
    }
  }

  const action = phase === 'starting' ? 'cancel' : phase === 'listening' ? 'stop' : phase === 'stopping' ? 'finishing' : 'start';
  return (
    <div className={`VoiceInput ${active ? 'VoiceInput--active' : ''}`}>
      <div className="VoiceInputRow">
        <button className="VoiceInputButton" type="button" onClick={toggle} aria-pressed={active} disabled={disabled || !Recognition || phase === 'stopping'} aria-describedby="voice-input-help">
          <StudioIcon name={active ? 'stop' : 'microphone'} size={18} />
          {t(`voice.${action}`)}
        </button>
        <select aria-label={t('voice.languageLabel')} value={language} onChange={(event) => setLanguage(event.target.value)} disabled={disabled || active || !Recognition}>
          <option value="en-US">English</option>
          <option value="ar-SA">العربية</option>
        </select>
      </div>
      <p className="VoiceInputHelp" id="voice-input-help">{t(Recognition ? 'voice.privacy' : 'voice.unsupported')}</p>
      <div className="VoiceInputStatus" aria-live="polite">
        {active || message ? <span role="status">{active ? <><i aria-hidden="true" />{t(`voice.${phase}`)}</> : t(`voice.${message}`)}</span> : null}
      </div>
      {interim ? <p className="VoiceInputInterim" dir="auto" aria-live="off">{interim}</p> : null}
      {error ? <p className="VoiceInputError" role="alert">{t(`voice.${error}`)}</p> : null}
    </div>
  );
}
